// @ts-nocheck
/* ============================================================
   Fence Works — calculation engine (pure, client-side)
   Reproduces the spec math exactly for the default product.
   Exposed as window.VFP
   ============================================================ */
import { runGradePct, tierFor, GRADE_FACTORS, runElevSamples, samplesGradePct, cornerElevations } from "./grade";   // measured grade profiles + corner elevations
(function () {
  "use strict";

  // ---- formatting helpers ----
  const money = (n) =>
    "$" + (Math.round(n * 100) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const money0 = (n) =>
    "$" + Math.round(n).toLocaleString("en-US");
  const ft = (n) => `${Math.round(n).toLocaleString("en-US")} ft`;

  // ---- gate size tiers ----
  // A gate is a size-dependent PRODUCT: its material cost comes from the
  // product's tiered gate price sheet (_editor.gates: { walk, drive10, drive12 }),
  // picked by the gate's WIDTH. Cutoffs (a design call — tune here):
  //   ≤6 ft → walk · >6 & ≤11 ft → drive10 · >11 ft → drive12
  const GATE_TIERS = [
    { key: "walk",    label: "Walk Gate",        max: 6  },
    { key: "drive10", label: "10 ft Drive Gate", max: 11 },
    { key: "drive12", label: "12 ft Drive Gate", max: Infinity },
  ];
  const gateTierFor = (widthFt) => {
    const w = Number(widthFt) || 0;
    return (GATE_TIERS.find(t => w <= t.max) || GATE_TIERS[0]).key;
  };
  // Resolve a tier key → material cost from the product. Prefer the tiered
  // price; if that tier is blank, fall back to the nearest non-empty tier
  // (walk → drive10 → drive12 and back), then to the flat p.gateCost.
  const gateMaterialFor = (product, tierKey) => {
    const g = (product && product._editor && product._editor.gates) || {};
    const order = ["walk", "drive10", "drive12"];
    const num = (v) => { const n = parseFloat(v); return isFinite(n) && n > 0 ? n : 0; };
    // a tier's cost: the gate-options LIST when one exists (the ◉ default line
    // for that size, else its first priced line), else the legacy flat tier $
    const tierVal = (k) => {
      const list = Array.isArray(g.list) ? g.list.filter(e => e && e.tier === k && num(e.cost)) : [];
      if (list.length) { const d = list.find(e => e.default) || list[0]; return Math.round(num(d.cost) * 100) / 100; }
      return num(g[k]);
    };
    if (tierVal(tierKey)) return tierVal(tierKey);
    // nearest non-empty tier, scanning outward from the requested one
    const idx = Math.max(0, order.indexOf(tierKey));
    for (let d = 1; d < order.length; d++) {
      const lo = order[idx - d], hi = order[idx + d];
      if (lo && tierVal(lo)) return tierVal(lo);
      if (hi && tierVal(hi)) return tierVal(hi);
    }
    return num(product && product.gateCost);   // flat fallback (built-ins w/o a price sheet)
  };

  // ---- product catalog ----
  // The default reproduces the spec's unit costs exactly.
  const PRODUCTS = [
    {
      id: "vinyl-6x8-steel",
      name: "6×8 White Vinyl Privacy",
      sub: "Steel-Post Reinforced",
      category: "Vinyl",
      color: "#e9edf2",
      panelWidth: 8,
      panelCost: 0, postKit: 0, sleeveCost: 0, centering: 0, adhesiveCost: 0, adhesivePerPosts: 999,
      concretePerBag: 0, bagsPerPost: 0,
      gateCost: 450, allSteel: false,
      _editor: {
        category: "Vinyl", style: "Solid Privacy", color: "White", height: 6, panelLength: 8, flatRate: false,
        components: [
          // Post-type rows itemize PER POST (posts are shared between panels);
          // everything else itemizes per panel. One steel pipe slides inside
          // each vinyl post, with two centering collars on it — qty 2 here
          // used to double the pipes (2 × every post = 94 pipes on 47 posts).
          { type: "Post", desc: "Post kit", qty: 1, unit: 42.97, scales: false },
          { type: "Panel/Insert", desc: "6×8 White Vinyl Privacy panel", qty: 1, unit: 99, scales: false },
          { type: "Post", desc: "Galvanized Steel Post - 2-3/8\" Schedule 40 - 8' length", qty: 1, unit: 35, scales: false },
          { type: "Post", desc: "Centering Fittings - Steel Post", qty: 2, unit: 3, scales: false },
          { type: "Fittings", desc: "Misc hardware — glue, screws, rivets & bolts", qty: 1, unit: 4.5, scales: false },
        ],
        gates: { walk: "450", drive10: "", drive12: "" },
      },
    },
    {
      id: "vinyl-6x6-steel",
      name: "6×6 White Vinyl Privacy",
      sub: "All-Steel Reinforced",
      category: "Vinyl",
      color: "#e9edf2",
      panelWidth: 6,
      panelCost: 62.27,
      postKit: 22.22,     // cap / brackets / screws
      sleeveCost: 35.0,   // steel sleeve every post
      centering: 5.0,     // centering pieces per post
      adhesiveCost: 5.55, // PVC adhesive (1 per ~13 posts)
      adhesivePerPosts: 13,
      concretePerBag: 5.0,
      bagsPerPost: 2,
      gateCost: 213.59,   // walk gate material
      allSteel: true,
    },
    {
      id: "cedar-6-privacy",
      name: "6 ft Cedar Privacy",
      sub: "Dog-Ear, pressure-treated posts",
      category: "Wood",
      color: "#b87d4b",
      panelWidth: 8,
      panelCost: 41.0,
      postKit: 14.5,
      sleeveCost: 0,
      centering: 0,
      adhesiveCost: 0,
      adhesivePerPosts: 999,
      concretePerBag: 5.75,
      bagsPerPost: 2,
      gateCost: 165.0,
      allSteel: false,
    },
    {
      id: "chainlink-4",
      name: "4 ft Chain Link",
      sub: "Galvanized, residential",
      category: "Chain Link",
      color: "#9aa6b8",
      panelWidth: 10,
      panelCost: 38.0,
      postKit: 12.0,
      sleeveCost: 0,
      centering: 0,
      adhesiveCost: 0,
      adhesivePerPosts: 999,
      concretePerBag: 5.0,
      bagsPerPost: 1,
      gateCost: 120.0,
      allSteel: false,
    },
    {
      id: "alum-ornamental",
      name: "Aluminum Ornamental",
      sub: "Powder-coat black, 3-rail",
      category: "Iron/Aluminum",
      color: "#2b3340",
      panelWidth: 6,
      panelCost: 74.0,
      postKit: 28.0,
      sleeveCost: 0,
      centering: 0,
      adhesiveCost: 0,
      adhesivePerPosts: 999,
      concretePerBag: 5.0,
      bagsPerPost: 2,
      gateCost: 245.0,
      allSteel: false,
    },
  ];

  const REMOVAL = {
    none:          { label: "None",                          rate: 0  },
    chainlink:     { label: "Chain link",                    rate: 4  },
    wood:          { label: "Wood — no footings",            rate: 5  },
    wood_concrete: { label: "Wood — concrete footings",      rate: 12 },
  };

  // ---- defaults ----
  const DEFAULT_SETTINGS = {
    markup: 2.0,            // customer price = cost × markup
    markupBase: "total",    // 'total' = (material + labor) × markup · 'material' = material × markup
    // crews: define a crew, its size and per-person wage. Loaded rate = size × wage.
    crews: [
      { id: "crew-2man", name: "2-Man Install Crew", size: 2, wage: 28, burdenPct: 0, hoursPerDay: 8, color: "#2dd4bf",
        foreman: "", phone: "", skills: ["Wood", "Vinyl", "Chain Link"], members: [] },
      { id: "crew-3man", name: "3-Man Install Crew", size: 3, wage: 30, burdenPct: 0, hoursPerDay: 8, color: "#4dd4ff",
        foreman: "", phone: "", skills: ["Iron/Aluminum", "Industrial", "Composite"], members: [] },
    ],
    activeCrewId: "crew-2man",
    // estimated CREW-HOURS to install 300 ft, by fence category (you tune these)
    laborHours: {
      Wood: 9, Vinyl: 11, "Chain Link": 7, "Iron/Aluminum": 13,
      Composite: 11, "Farm/Ranch": 6, Industrial: 14, Temporary: 4,
    },
    laborHoursDefault: 10,
    // legacy fallbacks
    laborCrewSize: 2,
    laborHoursPer300ft: 16,
    laborRate: 28,
    disposalFee: 150,
    clearingRate: 200,      // $/hr
    slopePct: 0.15,
    // quoting behavior flags. priceSlopeFromGrade: OFF by default — when ON,
    // a side carrying measured grade points (side.profile, ≥2 points) prices
    // its slope factor from those measurements instead of the grade dropdown.
    // gradeMode "pct" = % uplift from the per-side grade factors (default);
    // "flat" = a fixed $ per graded (non-level) run — simpler to quote.
    quoting: { priceSlopeFromGrade: false, gradeMode: "pct", gradeFlatPerRun: 350 },
    cornerSurcharge: 40,
    gateLabor: 125,         // per gate — legacy flat $; used until gateHours is tuned
    // per-gate install time by size tier (crew-hours). ALWAYS feeds the job's
    // labor-hours (scheduling, capacity, operator cost); prices the gate
    // line's labor (hours × loaded crew rate) once the contractor saves their
    // own values (settings.gateHours) — otherwise the flat gateLabor keeps
    // pricing, so existing quotes don't move.
    gateHours: { walk: 2, drive10: 4, drive12: 5 },
    permitFee: 100,
    // sales tax — decimal rate (0.055 = 5.5%). 0 = no tax (default; existing
    // quotes are unaffected). When set, it's added to the customer total, so
    // Stripe/the hub charge it, and it pushes to QuickBooks.
    taxRate: 0,
    taxLabel: "Sales tax",
    travelRate: 1.5,        // $/mi beyond free radius
    travelFree: 20,         // miles included
    rockNote: "Quote assumes drivable soil. Subsurface rock encountered during install is billed at $75/post.",
    googleReviewUrl: "",
    // consumer financing — stays OFF until the contractor connects their
    // Wisetack account (a dead "finance monthly" button just errors on
    // customers); the Financing page unlocks the toggle after connect.
    financing: {
      enabled: false,
      partner: "Wisetack",
      apr: 9.9,                 // representative APR shown to customer
      termMonths: 60,           // max term for the monthly estimate
      applyUrl: "",             // your affiliate / partner application link (earns referral kickback)
      kickbackNote: "Fence Works partners with lenders that pay a referral bonus when a customer you send signs up — turning financing into a small revenue stream.",
    },
    // editable email sequences (merge tags: {{customer}} {{company}} {{review_link}} {{address}})
    sequences: [
      { id: "seq-review", name: "Review Request", trigger: "job_completed", on: true, steps: [
        { delayDays: 0, subject: "How did we do, {{customer}}?", body: "Hi {{customer}},\n\nThank you for trusting {{company}} with your new fence — it was a pleasure working with you.\n\nIf you have a moment, a quick Google review helps a local business like ours more than you know:\n{{review_link}}\n\nThank you!\n{{company}}" },
        { delayDays: 5, subject: "A quick favor 🙏", body: "Hi {{customer}},\n\nJust circling back — if you were happy with your fence, we'd be grateful for a short review:\n{{review_link}}\n\nIt takes 30 seconds and truly helps. Thank you!\n{{company}}" },
      ] },
      { id: "seq-referral", name: "Referral Ask · $100 Visa Bonus", trigger: "job_completed", on: true, reward: "$100 Visa gift card", steps: [
        { delayDays: 0, subject: "Refer a friend, get a $100 Visa gift card 🎁", body: "Hi {{customer}},\n\nThank you again for trusting {{company}} with your new fence! We hope you love it.\n\nMost of our work comes from happy customers like you — so here's our thank-you: refer a friend or neighbor, and when they book a fence with us, we'll send you a {{reward}}. There's no limit — every referral that books earns you another $100.\n\nJust reply with their name and number, or have them mention you when they call.\n\n— {{company}}" },
        { delayDays: 2, subject: "Know a neighbor who needs a fence? ($100 for you)", body: "Hi {{customer}},\n\nNow that your fence is in, your neighbors have probably noticed! If anyone asks who did it, send them our way.\n\nEvery referral that books a project earns you a {{reward}} — our way of saying thanks.\n\nReply anytime with a name and number and we'll take great care of them.\n\n— {{company}}" },
        { delayDays: 30, subject: "Still earning you $100 per referral", body: "Hi {{customer}},\n\nHope the fence is holding up beautifully! A quick reminder that our referral bonus is still going strong — refer anyone who needs fencing and you'll get a {{reward}} when they book.\n\nKnow someone planning a project this season? We'd love an introduction.\n\n— {{company}}" },
        { delayDays: 60, subject: "One more way to earn $100 with {{company}}", body: "Hi {{customer}},\n\nIt's been about 90 days since we built your fence — thank you again for your business! Our {{reward}} referral bonus never expires, so if a friend, family member, or neighbor ever needs fencing, send them our way and we'll thank you with $100.\n\nWe appreciate you!\n\n— {{company}}" },
      ] },
      { id: "seq-quote", name: "Quote Follow-up", trigger: "quote_sent", on: true, steps: [
        { delayDays: 2, subject: "Following up on your fence quote", body: "Hi {{customer}},\n\nJust checking in on the quote we sent over for {{address}}. Happy to answer questions or adjust anything to fit your budget.\n\n— {{company}}" },
        { delayDays: 6, subject: "Still here when you're ready", body: "Hi {{customer}},\n\nNo rush at all — just letting you know your quote is still good and we'd love to build your fence when the timing's right.\n\n— {{company}}" },
        { delayDays: 12, subject: "Spread the cost — financing available 💳", body: "Hi {{customer}},\n\nWanted to make sure you knew financing is an option for your fence at {{address}}. Qualified homeowners can get approved in minutes with $0 down and budget-friendly monthly payments — checking your rate won't affect your credit.\n\nReply here and I'll send your secure application link, or let me know if you'd like to move forward.\n\n— {{company}}" },
        { delayDays: 21, subject: "Last check-in on your fence quote", body: "Hi {{customer}},\n\nI don't want to crowd your inbox, so this is my last note for now. Your quote for {{address}} is still good, and between flexible scheduling and easy monthly financing we can usually fit a new fence into most budgets.\n\nWhenever you're ready — even months from now — just reply and we'll pick right back up.\n\n— {{company}}" },
      ] },
    ],
  };

  /* ------------------------------------------------------------
     computeQuote(input, product, settings)
     input: {
       linearFeet, corners, gates,
       removal: 'none'|'chainlink'|'wood'|'wood_concrete',
       clearingHours, slope(bool), rockClause(bool),
       permit(bool), travelMiles
     }
  ------------------------------------------------------------ */
  function computeQuote(input, product, settings) {
    const p = product || PRODUCTS[0];
    const s = Object.assign({}, DEFAULT_SETTINGS, settings || {});
    const i = Object.assign(
      { linearFeet: 0, corners: 0, gates: 0, gateSizes: null, removal: "none", clearingHours: 0, slope: false, rockClause: false, permit: false, travelMiles: 0, jchannel: false },
      input || {}
    );

    const LF = Math.max(0, i.linearFeet);
    const corners = Math.max(0, i.corners | 0);
    // ---- per-gate widths (drive the size-dependent "Gate Installed" line) ----
    // Prefer explicit widths: i.gateSizes (passed by the estimator/snapshot),
    // else the drawn gates' widths (parcel.gates[].width). When only a count is
    // known (manual quotes), price every gate at the default walk width so the
    // count still produces a line. The count follows the widths when we have them.
    const DEFAULT_GATE_W = 4;   // ft — a typical walk gate; used when a width is missing
    // each entry stays as ONE gate (count-preserving); a missing/zero width just
    // defaults to a walk gate rather than dropping the gate from the estimate.
    const widthsFrom = (arr) => arr.map(w => { const n = Number(w) || 0; return n > 0 ? n : DEFAULT_GATE_W; });
    let gateWidths = Array.isArray(i.gateSizes) && i.gateSizes.length ? widthsFrom(i.gateSizes) : [];
    if (!gateWidths.length && i.parcel && Array.isArray(i.parcel.gates) && i.parcel.gates.length) {
      gateWidths = widthsFrom(i.parcel.gates.map(g => g && g.width));
    }
    const gates = gateWidths.length ? gateWidths.length : Math.max(0, i.gates | 0);
    if (!gateWidths.length && gates > 0) gateWidths = Array.from({ length: gates }, () => DEFAULT_GATE_W);

    // ---- quantities ----
    // Per-run is most accurate: each straight run rounds UP to whole panels,
    // and a run of N panels needs N+1 posts (a closed loop shares them: N posts).
    let runs = Array.isArray(i.runs) ? i.runs.filter(r => r > 0) : null;
    // measured-grade slope length: a graded run rides the hypotenuse √(L²+H²),
    // so it installs more fence than its flat length. Compute the added length
    // always (for the note); when grade-pricing is ON, the counts ride it too.
    const priceSlopeOn = !!(s.quoting && s.quoting.priceSlopeFromGrade);
    let slopeLenAddedFt = 0, slopeRuns = 0, slopeApplied = false;
    if (runs && runs.length && i.parcel && Array.isArray(i.parcel.sides)) {
      const E = cornerElevations(i.parcel);
      if (E) {
        const nC = E.length;
        const sel = i.parcel.sides.filter(sd => sd && sd.selected && (Number(sd.lf) || 0) > 0);
        if (sel.length === runs.length) {
          const slopeRunLens = sel.map((sd) => {
            const L = Math.max(0, Number(sd.lf) || 0);
            const dropFt = Math.abs((+E[(sd.i + 1) % nC] || 0) - (+E[sd.i] || 0)) / 12;
            const slope = Math.sqrt(L * L + dropFt * dropFt);
            if (slope - L > 0.05) { slopeLenAddedFt += slope - L; slopeRuns++; }
            return slope;
          });
          if (priceSlopeOn && slopeLenAddedFt > 0) { runs = slopeRunLens; slopeApplied = true; }
        }
      }
    }
    const closed = !!i.closed;
    let panels, posts, lineFeet;
    if (runs && runs.length) {
      lineFeet = runs.reduce((t, r) => t + r, 0);
      panels = runs.reduce((t, r) => t + Math.ceil(r / p.panelWidth), 0);
      posts = panels + (closed ? 0 : runs.length); // each open run adds an end post
      if (posts < panels) posts = panels;
    } else {
      lineFeet = LF;
      panels = LF > 0 ? Math.ceil(LF / p.panelWidth) : 0;
      posts = panels > 0 ? panels + 1 + corners : 0;
    }
    const sleeves = p.allSteel ? posts : 0;
    const adhesiveUnits = posts > 0 ? Math.ceil(posts / p.adhesivePerPosts) : 0;

    // ---- material line items ----
    const mat = [];
    const add = (key, label, qty, unit, sub, extra) => {
      if (qty <= 0 || unit <= 0) return;
      mat.push(Object.assign({ key, label, qty, unit, total: qty * unit, sub }, extra || {}));
    };

    const ed = p._editor;
    let concreteBags = 0;
    if (ed && Array.isArray(ed.components) && ed.components.length && !ed.flatRate) {
      // ---- itemize straight from the product's component cost-sheet (per panel × panels) ----
      // Posts/concrete are shared between panels → count by posts, not panels. Everything
      // else (rails, pickets, panels, fittings, fasteners) is per panel section.
      ed.components.forEach((c, idx) => {
        if (c.type === "Labor") return;
        const per = +c.qty || 0, unit = +c.unit || 0;
        if (per <= 0 || unit <= 0) return;
        const sharedByPost = (c.type === "Post" || c.type === "Concrete");
        const mult = sharedByPost ? posts : panels;
        const qty = Math.round(per * mult);
        if (c.type === "Concrete") concreteBags += qty;
        // sku/supplier ride each material line → BOMs, POs & inventory matching
        add("c" + idx, c.desc || c.type, qty, unit, `${qty} × ${money(unit)}`, { sku: c.sku || null, supplier: c.supplier || null });
      });
      // gate material is NO LONGER folded into the fence base — it's priced on
      // its own "Gate Installed" line (below) so the gate shows as its own charge.
    } else if (ed && ed.flatRate) {
      // flat $/ft product — single material line, no component itemization
      const perFt = +ed.flatPerFt || 0;
      add("flat", `${p.name} — material`, Math.round(lineFeet), perFt, `${Math.round(lineFeet)} ft × ${money(perFt)}/ft`);
    } else {
      // ---- legacy generic path (built-in products without a cost sheet) ----
      concreteBags = posts * p.bagsPerPost;
      add("panel", `${p.name} panel`, panels, p.panelCost, `${panels} × ${money(p.panelCost)}`);
      add("post", "Post kit (cap / brackets / screws)", posts, p.postKit, `${posts} × ${money(p.postKit)}`);
      if (p.allSteel) add("sleeve", "Steel sleeve (every post)", sleeves, p.sleeveCost, `${sleeves} × ${money(p.sleeveCost)}`);
      if (p.centering > 0) add("centering", "Centering pieces", posts, p.centering, `${posts} × ${money(p.centering)}`);
      add("concrete", "Concrete bags", concreteBags, p.concretePerBag, `${concreteBags} bags × ${money(p.concretePerBag)}`);
      if (p.adhesiveCost > 0) add("adhesive", "PVC adhesive", adhesiveUnits, p.adhesiveCost, `1 per ${p.adhesivePerPosts} posts`);
      // gate material priced separately on its own "Gate Installed" line (below).
    }

    const materialTotal = mat.reduce((a, m) => a + m.total, 0);

    // ---- internal labor estimate (crew-based) ----
    const crew = (s.crews || []).find(c => c.id === s.activeCrewId) || (s.crews || [])[0]
                 || { name: "Crew", size: s.laborCrewSize || 2, wage: s.laborRate || 28 };
    const crewRate = crewLoadedRate(crew) || ((Number(crew.size) || 2) * (Number(crew.wage) || 28));   // loaded $/hr for the whole crew
    const hoursPer300 = (s.laborHours && s.laborHours[p.category] != null)
                          ? s.laborHours[p.category] : (s.laborHoursDefault || 10);
    const fenceLaborHours = LF > 0 ? (LF / 300) * hoursPer300 : 0;   // crew-hours, footage only
    // per-gate install time by size tier — the crew really does spend these
    // hours, so scheduling / capacity / operator cost all see them
    const ghTier = Object.assign({}, DEFAULT_SETTINGS.gateHours, s.gateHours || {});
    const gateLaborHours = gateWidths.reduce((a, w) => a + (Number(ghTier[gateTierFor(w)]) || 0), 0);
    const laborHours = fenceLaborHours + gateLaborHours;   // crew-hours on site
    const laborCost = laborHours * crewRate;

    // ---- base customer price = cost × markup ----
    // fence-only labor here: the gate's labor is charged on its own "Gate
    // Installed" line — footing the base on laborCost would bill it twice
    const markupCostBase = s.markupBase === "material" ? materialTotal : (materialTotal + fenceLaborHours * crewRate);
    const base = markupCostBase * s.markup;

    // ---- add-on line items (stack on top of base) ----
    const addons = [];
    const pushAddon = (key, label, amount, note, customerFacing = true) => {
      if (amount <= 0) return;
      addons.push({ key, label, amount, note, customerFacing });
    };
    // BOM-only extras: quantities the crew must bring for an add-on, carried
    // separately so they NEVER fold into materialTotal/base price (the add-on
    // line item is the only place the cost lives). quoteBom() appends these.
    const bomExtras = [];

    const rem = REMOVAL[i.removal] || REMOVAL.none;
    let removalCost = 0, disposalCost = 0;
    if (rem.rate > 0 && LF > 0) {
      removalCost = rem.rate * LF;
      disposalCost = s.disposalFee;
      pushAddon("removal", `Old fence removal — ${rem.label}`, removalCost, `${ft(LF)} × ${money(rem.rate)}/ft`);
      pushAddon("disposal", "Haul-off & disposal", disposalCost, "flat fee");
    }

    const clearingCost = Math.max(0, i.clearingHours) * s.clearingRate;
    pushAddon("clearing", "Land / brush clearing", clearingCost, `${i.clearingHours} hr × ${money(s.clearingRate)}/hr`);

    // ---- U-channel post trim (premium, VINYL PRIVACY ONLY) ----
    // One U-channel piece at each spot a panel meets a post (every panel
    // touches 2 posts → qty = 2 × panels). Material each (incl. screws) and
    // labor minutes each are configurable per product (_editor.uchannelCost /
    // .uchannelLaborMin), defaulting to $4 and 1.5 min when unset; labor runs
    // at the job's crew rate. Customer price uses the same
    // (material + labor) × markup as the base. Off by default; only computes
    // for vinyl, and the cost lives ONLY in this add-on line (the BOM extra
    // below carries the qty for the crew, never folded into materialTotal).
    if (i.jchannel && p.category === "Vinyl" && panels > 0) {
      const jcQty = 2 * panels;
      // per-estimate $/channel override (typed under the toggle) → product default → $4
      const jcPer = (i.uchannelCost != null && i.uchannelCost !== "" && isFinite(+i.uchannelCost))
        ? Math.max(0, +i.uchannelCost)
        : ((+(p._editor && p._editor.uchannelCost)) || 4);
      const jcMaterialCost = jcPer * jcQty;
      const jcLaborHours = ((+(p._editor && p._editor.uchannelLaborMin) || 1.5) / 60) * jcQty;
      const jcLaborCost = jcLaborHours * crewRate;
      const jcCustomer = (jcMaterialCost + jcLaborCost) * s.markup;
      pushAddon("jchannel", "U-channel post trim — clean panel edges", jcCustomer, `${jcQty} channels · 2 per panel`, true);
      bomExtras.push({ item: "U-channel post trim", qty: jcQty });
    }

    const cornerCost = corners * s.cornerSurcharge;
    pushAddon("corners", "Corner posts", cornerCost, `${corners} × ${money(s.cornerSurcharge)}`);

    // ---- "Gate Installed" line(s) — a gate is a size-dependent product ----
    // Each gate's material comes from the product's tiered price sheet (by width),
    // plus the per-gate install labor (s.gateLabor). The whole thing is marked up
    // like the fence base — (material + labor) × markup — and shown as its OWN
    // customer line, grouped by tier (one line per tier: walk / 10 ft / 12 ft).
    // NOTE: labor is now INSIDE the markup (it was a flat, un-marked-up line before),
    // so the gate total is higher than the old "Gate install labor" line.
    let gateCostTotal = 0;   // gate material + labor AT COST (folds into operatorCost for margin)
    if (gateWidths.length) {
      const byTier = {};   // tierKey → { count, widths[] }
      gateWidths.forEach(w => {
        const t = gateTierFor(w);
        (byTier[t] || (byTier[t] = { count: 0, widths: [] })).count++;
        byTier[t].widths.push(w);
      });
      GATE_TIERS.forEach(({ key, label }) => {
        const grp = byTier[key]; if (!grp || grp.count <= 0) return;
        const mat = gateMaterialFor(p, key);
        // labor: tuned per-tier hours × loaded crew rate once the contractor
        // saved gate times; the legacy flat $/gate otherwise (price stability)
        const perGateLabor = s.gateHours ? (Number(ghTier[key]) || 0) * crewRate : s.gateLabor;
        const cost = grp.count * (mat + perGateLabor);
        const amount = cost * s.markup;
        // material only — the labor side already rides in laborCost (real hours)
        gateCostTotal += grp.count * mat;
        // sub: "{n} × {width} ft" (one width) or "{n} × {min}–{max} ft" (mixed)
        const lo = Math.min(...grp.widths), hi = Math.max(...grp.widths);
        const wTxt = lo === hi ? `${lo} ft` : `${lo}–${hi} ft`;
        pushAddon("gate-" + key, `${label} — installed`, amount, `${grp.count} × ${wTxt}`, true);
        bomExtras.push({ item: label, qty: grp.count });
      });
    }

    // per-estimate override (typed under the toggle) wins over the workspace default
    const permitCost = i.permit ? (i.permitFee != null && i.permitFee !== "" && isFinite(+i.permitFee) ? Math.max(0, +i.permitFee) : s.permitFee) : 0;
    pushAddon("permit", "Permit fee (pass-through)", permitCost, "at cost");

    const travelMiles = Math.max(0, i.travelMiles - s.travelFree);
    const travelCost = travelMiles * s.travelRate;
    pushAddon("travel", "Travel", travelCost, `${travelMiles} mi beyond ${s.travelFree} × ${money(s.travelRate)}`);

    const addonTotal = addons.reduce((a, x) => a + x.amount, 0);

    // ---- slope surcharge (% uplift on running total, or a per-estimate flat $) ----
    // i.slopeAdj = { mode: "pct"|"flat", value } — the number typed under the
    // toggle on this estimate. Absent → the workspace default % (settings).
    const preSlope = base + addonTotal;
    const slopeAdj = (i.slopeAdj && typeof i.slopeAdj === "object" && isFinite(+i.slopeAdj.value)) ? i.slopeAdj : null;
    let slopeAmount = 0, effSlopePct = s.slopePct, slopeSub = `+${Math.round(s.slopePct * 100)}%`;
    if (i.slope) {
      if (slopeAdj && slopeAdj.mode === "flat") {
        slopeAmount = Math.max(0, +slopeAdj.value);
        effSlopePct = null; slopeSub = "flat";
      } else {
        if (slopeAdj) effSlopePct = Math.max(0, +slopeAdj.value) / 100;
        slopeAmount = preSlope * effSlopePct;
        slopeSub = `+${Math.round(effSlopePct * 100)}%`;
      }
    }
    // ---- grade surcharge (per-side sloped/stepped panels) ----
    let gradePct = Math.max(0, i.gradePct || 0);
    // measured grade (Settings → Quoting, opt-in): a side carrying a surveyed
    // profile (≥2 drop/rise points) OR an absolute corner elevation on either
    // end (parcel.cornerElev — the contractor's post-height model) prices its
    // slope factor from the measured run grade — tierFor(% from lib/grade
    // runElevSamples, the same corner-chain + profile surface the 3D and the
    // site plan render) → GRADES factor — and the blended uplift mixes
    // measured and dropdown-graded sides by footage. With the flag off (or
    // nothing measured) the incoming gradePct is used untouched.
    const quoting = Object.assign({}, DEFAULT_SETTINGS.quoting, s.quoting || {});
    let measuredGradeSides = 0;
    // When the quote carries per-side data, ALWAYS re-blend the uplift from the
    // CURRENT grade dropdowns — i.gradePct is just the estimate-time snapshot,
    // and it goes stale the moment grades are edited on the quote page (runs
    // said "Level" while the price kept the old slope surcharge).
    if (i.parcel && Array.isArray(i.parcel.sides)) {
      const selNow = i.parcel.sides.filter(sd => sd && sd.selected);
      let numD = 0, denD = 0;
      selNow.forEach(sd => { const lfSide = Math.max(0, Number(sd.lf) || 0); numD += lfSide * (GRADE_FACTORS[sd.grade] || 0); denD += lfSide; });
      if (denD > 0) gradePct = numD / denD;
    }
    if (quoting.priceSlopeFromGrade && i.parcel && Array.isArray(i.parcel.sides)) {
      const sel = i.parcel.sides.filter(sd => sd && sd.selected);
      const ce = (i.parcel.cornerElev && typeof i.parcel.cornerElev === "object") ? i.parcel.cornerElev : {};
      const nCorners = Array.isArray(i.parcel.polygon) && i.parcel.polygon.length ? i.parcel.polygon.length : i.parcel.sides.length;
      const cornerSet = (k) => isFinite(+ce[k]);
      const isMeasured = (sd) => (Array.isArray(sd.profile) && sd.profile.length >= 2) || cornerSet(sd.i) || cornerSet((sd.i + 1) % nCorners);
      if (sel.some(isMeasured)) {
        let num = 0, den = 0;
        sel.forEach(sd => {
          let factor;
          const lfSide = Math.max(0, Number(sd.lf) || 0);
          if (isMeasured(sd)) {
            const samples = runElevSamples(i.parcel, sd.i);
            const pct = samples ? samplesGradePct(samples, lfSide) : runGradePct(sd.profile, lfSide);
            factor = GRADE_FACTORS[tierFor(pct).grade] || 0;
          } else factor = GRADE_FACTORS[sd.grade] || 0;
          num += lfSide * factor; den += lfSide;
        });
        if (den > 0) { gradePct = num / den; measuredGradeSides = sel.filter(isMeasured).length; }
      }
    }
    let gradeAmount = gradePct > 0 ? preSlope * gradePct : 0;
    let gradeNote = `${Math.round(gradePct * 100)}% of runs on grade${measuredGradeSides ? " · field-measured" : ""}`;
    // flat-rate mode: a fixed $ per graded run — no footage-driven surprises
    // (Settings → Pricing & Markup). The quote page's Edit-estimate override
    // still hard-sets the line either way.
    if (quoting.gradeMode === "flat" && i.parcel && Array.isArray(i.parcel.sides)) {
      const graded = i.parcel.sides.filter(sd => sd && sd.selected && sd.grade && sd.grade !== "level").length;
      const per = Math.max(0, Number(quoting.gradeFlatPerRun) || 0);
      gradeAmount = graded * per;
      gradeNote = `${graded} graded run${graded === 1 ? "" : "s"} × ${money(per)}`;
    }
    if (gradeAmount > 0) addons.push({ key: "grade", label: "Slope / stepped panel work", amount: gradeAmount, note: gradeNote, customerFacing: true });

    // ---- subtotal (pre-tax) → sales tax → customer total ----
    const subtotal = preSlope + slopeAmount + gradeAmount;
    const taxRate = Math.max(0, Number(s.taxRate) || 0);
    const tax = Math.round(subtotal * taxRate * 100) / 100;
    const customerTotal = Math.round((subtotal + tax) * 100) / 100;

    // ---- operator margin (on the PRE-TAX subtotal; tax is pass-through, not revenue) ----
    const passThrough = permitCost + disposalCost + travelCost;
    // gate material + labor left materialTotal/laborCost (they're priced on the
    // "Gate Installed" line), so add their true cost back here to keep margin honest.
    const operatorCost = materialTotal + laborCost + gateCostTotal + passThrough;
    const grossProfit = subtotal - operatorCost;
    const marginPct = subtotal > 0 ? (grossProfit / subtotal) * 100 : 0;

    const notes = [];
    // per-estimate clause text (typed under the toggle) wins over the default note
    if (i.rockClause) notes.push((typeof i.rockNote === "string" && i.rockNote.trim()) ? i.rockNote.trim() : s.rockNote);
    if (measuredGradeSides > 0) notes.push(`Slope pricing set by field-measured grade points on ${measuredGradeSides} run${measuredGradeSides > 1 ? "s" : ""} (drop/rise shots recorded on the site plan).`);
    if (slopeLenAddedFt >= 0.5) notes.push(`Riding the grade adds about ${slopeLenAddedFt < 2 ? slopeLenAddedFt.toFixed(1) : Math.round(slopeLenAddedFt)} ft of fence across ${slopeRuns} sloped run${slopeRuns > 1 ? "s" : ""} — the run follows the slope, not the flat line. ${slopeApplied ? "Included in the panel & post counts." : "Field-verify length & panels on the graded runs."}`);

    return {
      LF, panels, posts, sleeves, concreteBags, adhesiveUnits, gates, corners,
      panelWidth: p.panelWidth, runCount: runs ? runs.length : 1, closed,
      mat, materialTotal, bomExtras,
      base, markup: s.markup, markupBase: s.markupBase, markupCostBase,
      crew, crewRate, hoursPer300, laborHours, laborCost,
      addons, addonTotal,
      slope: i.slope, slopeAmount, slopePct: effSlopePct != null ? effSlopePct : s.slopePct, slopeSub,
      gradePct, gradeAmount, measuredGradeSides,
      subtotal, tax, taxRate, taxLabel: s.taxLabel || "Sales tax",
      customerTotal,
      operatorCost, passThrough, grossProfit, marginPct,
      notes,
      product: p,
    };
  }

  // ---- shared crew rate (burden-aware loaded $/hr for the whole crew) ----
  // If the crew has named members with individual wages, the rate is the
  // sum of those wages; otherwise it falls back to size × base wage.
  function crewBaseRate(c) {
    if (!c) return 0;
    const members = c.members || [];
    const withWage = members.filter(m => Number(m.wage) > 0);
    if (members.length > 0 && withWage.length > 0) {
      // every member contributes; missing wages fall back to the crew base wage
      return members.reduce((sum, m) => sum + (Number(m.wage) > 0 ? Number(m.wage) : (Number(c.wage) || 0)), 0);
    }
    return (Number(c.size) || 0) * (Number(c.wage) || 0);
  }
  function crewHeadcount(c) {
    if (!c) return 0;
    return (c.members && c.members.length) ? c.members.length : (Number(c.size) || 0);
  }
  function crewLoadedRate(c) {
    if (!c) return 0;
    const burden = Number(c.burdenPct) || 0;
    return crewBaseRate(c) * (1 + burden / 100);
  }

  // ---- monthly payment for consumer financing (amortized) ----
  function monthlyPayment(principal, apr, months) {
    const P = Number(principal) || 0, n = Number(months) || 0;
    if (P <= 0 || n <= 0) return 0;
    const r = (Number(apr) || 0) / 100 / 12;
    if (r === 0) return P / n;
    return P * r / (1 - Math.pow(1 + r, -n));
  }

  window.VFP = { PRODUCTS, REMOVAL, DEFAULT_SETTINGS, computeQuote, crewLoadedRate, crewBaseRate, crewHeadcount, monthlyPayment, money, money0, ft };
})();
